import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProtocolVersion} from 'notebook/ELNConstants';
import {MixtureIngredientsStructuredTable} from 'notebook/integrations/registry/MixtureIngredientsStructuredTableAdapter';
import {MixtureMetadataStructuredTable} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import {
  NoteLinkedGroup,
  NoteLinkedGroupKind,
  UnknownNoteLinkedGroup,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import type {SimpleTableDoc} from 'notebook/simple-tables/useSimpleEvaluation';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {
  AnyStructuredTable,
  isNotePartWithItem,
  Note,
  NoteLinkedFile,
  NoteLinkedObject,
  NoteLinkedObjectType,
  NotePart,
  NotePartItem,
  NotePartType,
  NotePartWithItem,
} from 'notebook/util/NoteUtilConstants';
import {NoteLinkedAssayData} from 'runs/AttachmentConstants';

import type {StoichiometryGroup} from './integrations/chemistry/StoichiometryNoteLinkedGroupAdapter';
import {MixtureGroupMetadata} from './integrations/registry/MixtureGroupConstants';
import {NoteFillInTextBox} from './notes/plugins/fill-in-text-box/constants/FillInTextBoxConstants';

export function hasNLO(notePart: NotePart): notePart is NotePart & {item: NoteLinkedObject} {
  return notePart.type === ModelTypeConstants.NOTE_LINKED_OBJECT && notePart.item != null;
}

export function isNoteLinkedFile(notePartItem: NotePartItem): notePartItem is NoteLinkedFile {
  return (
    // eslint-disable-next-line benchlint/no-in-operator
    notePartItem && 'type' in notePartItem && notePartItem.type === NoteLinkedObjectType.NOTE_LINKED_FILE
  );
}

export function isStructuredTable(notePartItem: NotePartItem): notePartItem is AnyStructuredTable {
  return (
    // eslint-disable-next-line benchlint/no-in-operator
    notePartItem && 'type' in notePartItem && notePartItem.type === NoteLinkedObjectType.STRUCTURED_TABLE
  );
}

export function isNoteFillInTextBox(notePartItem: NotePartItem): notePartItem is NoteFillInTextBox {
  return (
    // eslint-disable-next-line benchlint/no-in-operator
    notePartItem && 'type' in notePartItem && notePartItem.type === NoteLinkedObjectType.NOTE_FILL_IN_TEXT_BOX
  );
}

export function isNoteLinkedGroup(notePartItem: NotePartItem): notePartItem is UnknownNoteLinkedGroup {
  return (
    // eslint-disable-next-line benchlint/no-in-operator
    notePartItem && 'type' in notePartItem && notePartItem.type === NoteLinkedObjectType.NOTE_LINKED_GROUP
  );
}

export function isMixtureGroup(
  notePartItem: NotePartItem
): notePartItem is NoteLinkedGroup<MixtureGroupMetadata> {
  return isNoteLinkedGroup(notePartItem) && notePartItem.kind === NoteLinkedGroupKind.MIXTURE_GROUP;
}

export function isStoichiometryGroup(notePartItem: NotePartItem): notePartItem is StoichiometryGroup {
  return isNoteLinkedGroup(notePartItem) && notePartItem.kind === NoteLinkedGroupKind.STOICHIOMETRY_GROUP;
}

export function isMixtureMetadataStructuredTable(
  notePartItem: NotePartItem
): notePartItem is MixtureMetadataStructuredTable {
  return isStructuredTable(notePartItem) && notePartItem.kind === StructuredTableKind.MIXTURE_METADATA;
}

export function isMixtureIngredientsStructuredTable(
  notePartItem: NotePartItem
): notePartItem is MixtureIngredientsStructuredTable {
  return isStructuredTable(notePartItem) && notePartItem.kind === StructuredTableKind.MIXTURE_INGREDIENTS;
}

export function isNotePartWithMixtureMetadataStructuredTable(
  notePart: NotePart
): notePart is NotePartWithItem<MixtureMetadataStructuredTable> {
  return isNotePartWithItem(notePart) && isMixtureMetadataStructuredTable(notePart.item);
}

export function isNote(item: Note | ProtocolVersion | SimpleTableDoc): item is Note {
  return item.hasOwnProperty('noteParts');
}

export function isProtocol(item: Note | ProtocolVersion | SimpleTableDoc): item is ProtocolVersion {
  return item.hasOwnProperty('steps');
}

export function isSimpleTableDoc(item: Note | ProtocolVersion | SimpleTableDoc): item is SimpleTableDoc {
  return item.hasOwnProperty('fullTableMap');
}

export function isPlainTable(notePart: NotePart): boolean {
  // eslint-disable-next-line benchlint/no-in-operator
  return notePart && 'type' in notePart && notePart.type === NotePartType.TABLE;
}

export function isNoteLinkedAssayData(notePartItem: NotePartItem): notePartItem is NoteLinkedAssayData {
  return (
    notePartItem &&
    // eslint-disable-next-line benchlint/no-in-operator
    'type' in notePartItem &&
    notePartItem.type === NoteLinkedObjectType.NOTE_LINKED_ASSAY_DATA
  );
}
